import React from 'react';

import {AnalyticsVisualizationLegendLocation as LegendLocation} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {SCATTER_PLOT_DEFAULT_COLOR_PALETTE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {getRegressionTypeFromConfig} from 'insights/analytics-visualizations/data/getRegressionType';
import {isPointMaskingEnabled} from 'insights/analytics-visualizations/VisualizationEnablement';
import {VisualizationConfig} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {RegressionType} from 'ui-library/charting/statistics/regression/RegressionTypes';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {RegressionTypeChartParameters, VisualizationType} from '../AnalyticsVisualizationTypes';
import AggregationConfigEditor from './config-components/AggregationConfigEditor';
import ColorPaletteConfigEditor from './config-components/ColorPaletteConfigEditor';
import ErrorBarColumnEditor from './config-components/ErrorBarColumnEditor';
import ErrorBarConfigEditor from './config-components/ErrorBarConfigEditor';
import FormatConfigEditor from './config-components/FormatConfigEditor';
import MaskConfigEditor from './config-components/MaskConfigEditor';
import OutlierConfigEditor from './config-components/OutlierConfigEditor';
import RegressionConfigEditor from './config-components/RegressionConfigEditor';
import {SeriesColumnEditor, SeriesParameter, XYColumnEditor} from './config-components/XYSeriesColumnEditor';
import TabbedConfigEditorSkeleton from './TabbedConfigEditorSkeleton';
import {VisualizationConfigEditorComponentProps} from './VisualizationConfigEditorTypes';

type ScatterPlotParameters = ExtractByTypename<
  VisualizationConfig['parameters'],
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

export type ScatterPlotConfigEditorProps = VisualizationConfigEditorComponentProps<ScatterPlotParameters>;
export const SCATTER_PLOT_DEFAULT_LEGEND_LOCATION = LegendLocation.TOP;

export default function ScatterPlotConfigEditor(props: ScatterPlotConfigEditorProps): JSX.Element {
  const {
    onUpdateParameters,
    isAnalysisTool,
    parameters,
    visualizationConfig,
    enableAnalysisTab,
    enableFormatTab,
    enableAggregation = true,
    isDisabled = false,
    enableErrorBarColumn,
  } = props;

  function handleUpdateRegression(update: Partial<RegressionTypeChartParameters>): void {
    onUpdateParameters({
      ...update,
      ...((!update.regressionConfig ||
        getRegressionTypeFromConfig(update.regressionConfig) !== RegressionType.LOGISTIC) && {
        outlierConfig: null,
      }),
    });
  }

  function handleUpdateSeries(update: Partial<SeriesParameter>): void {
    let fullUpdate: Partial<ScatterPlotParameters> = update;
    if (update.seriesColumnName == null) {
      fullUpdate = {...fullUpdate, colorPalette: SCATTER_PLOT_DEFAULT_COLOR_PALETTE};
    }
    onUpdateParameters(fullUpdate);
  }

  return (
    <TabbedConfigEditorSkeleton
      setupTabContent={
        <>
          <XYColumnEditor {...props} />
          <SeriesColumnEditor
            {...props}
            errorMessage={
              getRegressionTypeFromConfig(parameters.regressionConfig) != null
                ? 'Remove the regression line to configure a series'
                : null
            }
            onUpdateParameters={handleUpdateSeries}
          />
          {enableErrorBarColumn && <ErrorBarColumnEditor {...props} />}
          <ColorPaletteConfigEditor
            parameters={parameters}
            onUpdateParameters={onUpdateParameters}
            isDisabled={isDisabled}
          />
        </>
      }
      analysisTabContent={
        <>
          {!isAnalysisTool && (
            <RegressionConfigEditor
              {...props}
              errorMessage={
                parameters.seriesColumnName != null
                  ? 'Remove the series configuration to add a regression line'
                  : null
              }
              onUpdateParameters={handleUpdateRegression}
            />
          )}
          {enableAggregation && (
            <>
              <AggregationConfigEditor parameters={parameters} onUpdateParameters={onUpdateParameters} />
              <ErrorBarConfigEditor parameters={parameters} onUpdateParameters={onUpdateParameters} />
            </>
          )}
          {!isAnalysisTool && (
            <OutlierConfigEditor
              {...props}
              errorMessage={
                getRegressionTypeFromConfig(parameters.regressionConfig) !== RegressionType.LOGISTIC
                  ? 'Apply a logistic regression line to detect outliers'
                  : null
              }
            />
          )}
          {!isAnalysisTool &&
            isPointMaskingEnabled() &&
            getRegressionTypeFromConfig(parameters.regressionConfig) != null && (
              <MaskConfigEditor {...props} />
            )}
        </>
      }
      formatTabContent={
        <>
          <FormatConfigEditor {...props} />
          {parameters.seriesColumnName != null && (
            <div className="u-input-spacing-top">
              <FieldLabel label="Legend" />
              <CheckboxInput
                id="show-legend"
                label="Show legend"
                className="u-mini-spacing-top"
                onChange={(e) => {
                  onUpdateParameters({
                    ...parameters,
                    legendLocation: e.target.checked ? SCATTER_PLOT_DEFAULT_LEGEND_LOCATION : null,
                  });
                }}
                checked={parameters.legendLocation != null}
              />
            </div>
          )}
        </>
      }
      enableAnalysisTab={enableAnalysisTab}
      enableFormatTab={enableFormatTab}
      visualizationConfigId={visualizationConfig.id}
      visualizationType={VisualizationType.SCATTER_PLOT}
    />
  );
}
